/**
 * Official profile · Overview (screenshots/19): Policy positions (filter by area; item · area · vote · stance chip
 * · verbatim quote · "Watch at"), Appearances (video frame, meeting, time range, summary, exchanges), and the side
 * column: Political leaning, Policy areas (ECharts), Contact.
 */
import { List, ListRow, RowBelow, RowLead, RowMain, RowSub, RowTitle, RowTrail } from "../components/shared/List";
import { useState } from "react";
import { ArrowRight, History, Landmark, Mail, MapPin, Phone, ScrollText, Tag } from "lucide-react";
import { CountBadge } from "../components/shared/ui";
import { Face } from "../components/shared/Face";
import { clock } from "../lib/format";
import { PersonImg, StanceChip, Watch, mdy } from "./atoms";
import { LeaningCard } from "./LeaningCard";
import { PolicyAreasChart } from "./PolicyAreasChart";
import type { PersonProfile, PolicyPosition } from "./model";

const SHOWN = 3;
const RELATION: Record<string, string> = { agree: "Echoed", disagree: "Disagreed with", question: "Questioned", thanks: "Thanked", yield: "Yielded to", other: "Spoke with" };

export function ProfileOverview({ p, canFlag, onAppearances }: { p: PersonProfile; canFlag: boolean; onAppearances: () => void }) {
  return (
    <div className="p-cols">
      <div className="p-main">
        <Positions positions={p.positions} />
        <section className="p-sec">
          <div className="p-sec-head"><h2>Appearances</h2><CountBadge>{p.appearances}</CountBadge></div>
          <AppearanceList p={p} onMore={onAppearances} />
        </section>
      </div>
      <aside className="p-side">
        {p.contact && (
          <section className="p-card p-contact">
            <h3>Contact · Ward {p.contact.ward}</h3>
            {p.contact.email && <a href={`mailto:${p.contact.email}`}><Mail size={15} aria-hidden />{p.contact.email}</a>}
            {p.contact.wardPhone && <a href={`tel:${p.contact.wardPhone.replace(/[^\d+]/g, "")}`}><Phone size={15} aria-hidden />{p.contact.wardPhone} · ward office</a>}
            {p.contact.wardAddress && <span><MapPin size={15} aria-hidden />{p.contact.wardAddress}</span>}
            {p.contact.cityHall && <span><Landmark size={15} aria-hidden />{p.contact.cityHall}</span>}
            {p.contact.election && <small>{p.contact.election}</small>}
          </section>
        )}
        {p.leaning && <LeaningCard personId={p.id} estimate={p.leaning} canFlag={canFlag} />}
        {p.areaShares.length > 0 && (
          <section className="p-card">
            <h3>Policy areas</h3>
            <p className="p-card__sub p-card__sub--md">Share of speaking time this term</p>
            <PolicyAreasChart shares={p.areaShares} />
          </section>
        )}
      </aside>
    </div>
  );
}

function Positions({ positions }: { positions: PolicyPosition[] }) {
  const [area, setArea] = useState<string | null>(null);
  const [all, setAll] = useState(false);
  const counts = new Map<string, number>();
  for (const x of positions) counts.set(x.area, (counts.get(x.area) ?? 0) + 1);
  const areas = [...counts.entries()].sort((a, b) => b[1] - a[1]);
  const list = positions.filter((x) => !area || x.area === area);
  const shown = all ? list : list.slice(0, SHOWN);
  return (
    <section className="p-sec">
      <div className="p-sec-head"><h2>Policy positions</h2><CountBadge>{positions.length}</CountBadge></div>
      <div className="p-pills" role="radiogroup" aria-label="Policy area">
        <button role="radio" aria-checked={!area} onClick={() => setArea(null)}>All areas</button>
        {areas.map(([a, n]) => <button key={a} role="radio" aria-checked={area === a} onClick={() => setArea(a)}>{a} · {n}</button>)}
      </div>
      <List variant="plain" className="p-pos-list">
      {shown.map((x) => {
        const e = x.evidence[0];
        return (
          <ListRow key={x.id} className="p-pos">
            <RowLead><i className="type-badge" data-type={x.recordId ? "legislation" : "topic"} aria-hidden>{x.recordId ? <ScrollText size={11} /> : <Tag size={11} />}</i></RowLead>
            <RowMain min={160} className="p-pos__title">
              <RowTitle>{x.recordId ? <a href={`/dashboard/legislation/${encodeURIComponent(x.recordId)}`}>{x.label}</a> : <b>{x.label}</b>}</RowTitle>
              <RowSub wrap><small>{x.recordId ? [x.area, x.recordId, x.outcome, x.vote ? `voted ${x.vote}` : null].filter(Boolean).join(" · ") : `${x.area} · topic`}</small></RowSub>
            </RowMain>
            <RowTrail><StanceChip stance={x.stance} /></RowTrail>
            {e && (
              <RowBelow>
                <blockquote className="p-quote">
                  <p>“{e.quote}”</p>
                  <footer>{e.meetingLabel}<Watch meetingId={e.meetingId} at={e.at}>Watch at {clock(e.at)}</Watch></footer>
                </blockquote>
              </RowBelow>
            )}
          </ListRow>
        );
      })}
      </List>
      {!all && list.length > SHOWN && <button className="link-arrow p-more-link" onClick={() => setAll(true)}>Show all {list.length} positions<ArrowRight size={14} aria-hidden /></button>}
    </section>
  );
}

export function AppearanceList({ p, all = false, onMore }: { p: PersonProfile; all?: boolean; onMore?: () => void }) {
  const list = all ? p.appearancesList : p.appearancesList.slice(0, 1);
  const rest = p.appearancesList.length - list.length;
  return (
    <div className="p-apps">
      {list.map((a) => (
        <div key={a.id} className="p-app">
          <PersonImg image={a.frame} name={p.name} w={154} h={87} label />
          <div className="p-app__body">
            <div className="p-app__meeting"><i className="p-dot" data-kind={/^City Council/.test(a.body) ? "council" : "committee"} aria-hidden /><b>{a.meetingLabel}</b><small>{mdy(a.date, { weekday: "short", month: "short", day: "numeric" })}</small></div>
            <p>{a.summary}</p>
            <div className="p-app__links">
              <Watch meetingId={a.meetingId} at={a.from}>Watch at {clock(a.from)}</Watch>
              {a.exchanges.map((x) => (
                <a key={`${x.personId}-${x.at}`} className="p-exchange" href={`/dashboard/meetings/${encodeURIComponent(a.meetingId)}?t=${clock(x.at)}`}>
                  <Face name={x.name} photoUrl={x.photoUrl} size={18} />{RELATION[x.relation] ?? RELATION.other} {x.name} at {clock(x.at)}
                </a>
              ))}
            </div>
          </div>
        </div>
      ))}
      {!all && rest > 0 && (
        <button className="other-meetings p-earlier" onClick={onMore}>
          <History size={16} aria-hidden /><span>{rest} earlier {rest === 1 ? "appearance" : "appearances"} this term</span><span className="other-meetings__show">Show</span>
        </button>
      )}
    </div>
  );
}

